import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Card,
  CardContent,
  toast,
} from "@lumanity/ui/primitives";
import { cn } from "@/lib/utils";
import { isConflict } from "@/services/api";
import GeneralGuidanceStep from "./GeneralGuidanceStep";
import TargetOutletStep from "./TargetOutletStep";
import { BRIEF_STEPS, isBriefStep, type BriefStepId, type SaveOutput } from "./briefSteps";
import { outputKeys, useUpdateOutput } from "./outputsApi";
import type { Output } from "./types";

interface OutputBriefProps {
  output: Output;
  /** Saved: start (or return to) the First draft and its outline. */
  onContinueToOutline: () => void;
}

/**
 * The Brief (FRD Stage 4): Target Outlet, then General guidance, with a mini
 * nav on the right. The current sub-step lives in `?step=` so it survives a reload.
 */
const OutputBrief = ({ output, onContinueToOutline }: OutputBriefProps) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStep = searchParams.get("step");
  const step: BriefStepId = isBriefStep(rawStep) ? rawStep : "outlet";
  const update = useUpdateOutput(output.id);

  // Ref (not state) so a navigation straight after a save sees the step as clean.
  const dirtyRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const handleDirtyChange = useCallback((d: boolean) => {
    dirtyRef.current = d;
    setDirty(d);
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyRef.current && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const goTo = (next: BriefStepId) => setSearchParams({ step: next });
  /** Leave the step without the unsaved-changes prompt (Skip discards on purpose; Continue has just saved). */
  const goToClean = (next: BriefStepId) => {
    handleDirtyChange(false);
    goTo(next);
  };

  const save: SaveOutput = async (patch, { toastOnSuccess } = {}) => {
    try {
      await update.mutateAsync({ version: output.version, ...patch });
      dirtyRef.current = false;
      if (toastOnSuccess) toast({ title: "Draft saved" });
      return true;
    } catch (e) {
      if (isConflict(e)) {
        toast({
          title: "Someone else saved this output",
          description: "We've loaded their latest version. Please re-apply your changes.",
          variant: "destructive",
        });
        handleDirtyChange(false);
        await queryClient.invalidateQueries({ queryKey: outputKeys.output(output.id) });
      } else {
        toast({ title: "Could not save", description: (e as Error).message, variant: "destructive" });
      }
      return false;
    }
  };

  const stepProps = { output, save, saving: update.isPending, onDirtyChange: handleDirtyChange };

  return (
    <div className="flex flex-col-reverse gap-6 lg:flex-row lg:items-start">
      <Card className="flex-1 min-w-0">
        <CardContent className="p-6">
          {/* Keyed on the version: after a save (or a conflict reload) the form restarts from the server's copy. */}
          {step === "outlet" ? (
            <TargetOutletStep
              key={`outlet-${output.version}`}
              {...stepProps}
              onSkip={() => goToClean("guidance")}
              onContinue={() => goToClean("guidance")}
            />
          ) : (
            <GeneralGuidanceStep key={`guidance-${output.version}`} {...stepProps} onContinue={onContinueToOutline} />
          )}
        </CardContent>
      </Card>

      <nav aria-label="Brief steps" className="lg:w-56 shrink-0 lg:sticky lg:top-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 mb-2">Brief</p>
        <ol className="flex lg:flex-col gap-1">
          {BRIEF_STEPS.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => s.id !== step && goTo(s.id)}
                aria-current={s.id === step ? "step" : undefined}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-left border-l-2 transition-colors",
                  s.id === step
                    ? "border-brand-teal bg-brand-light-teal text-brand-navy"
                    : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <span className="text-xs">{i + 1}.</span> {s.label}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <AlertDialog open={blocker.state === "blocked"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>You have unsaved changes on this step. They'll be lost if you leave now.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Stay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDirtyChange(false);
                blocker.proceed?.();
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutputBrief;
